/**
 * Desktop prev/next arrows for sections/evry-collection-slider.liquid's horizontal scroll track,
 * and centering of the active tile when the slider is a subcollection picker.
 * Touch/trackpad scrolling already works natively on the track's own overflow-x: auto — this is
 * a click affordance on top of that, not a replacement for it. A plain custom element, not a
 * @theme/component subclass: there's no refs/state complex enough to need that, and a merchant
 * can add this section more than once on the same page, so each instance manages its own track
 * independently rather than assuming a single global one (the pattern
 * snippets/cart-stack-carousel.liquid uses, safe there only because the cart drawer is a
 * singleton).
 */
class EvryCollectionSliderComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  connectedCallback() {
    this.#track = this.querySelector('.evry-collection-slider__track');
    if (!this.#track) return;

    this.#track.addEventListener('scroll', this.#updateArrows, { passive: true });
    this.addEventListener('click', this.#handleArrowClick);
    window.addEventListener('resize', this.#updateArrows);

    new MutationObserver(this.#updateArrows).observe(this.#track, { childList: true });

    this.#centerActiveTile();
    this.#updateArrows();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#updateArrows);
  }

  /**
   * On a subcollection (the slider's menu source), brings the tile for the page being viewed
   * into the middle of the row, as the 2024 picker did. Scrolls only the track, never the page:
   * scrollIntoView would also move the window to the slider.
   */
  #centerActiveTile() {
    const track = this.#track;
    const active = track?.querySelector('[aria-current="page"]');
    if (!track || !active) return;

    const trackRect = track.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const offset = activeRect.left + activeRect.width / 2 - (trackRect.left + trackRect.width / 2);
    track.scrollBy({ left: offset, behavior: 'instant' });
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  #tileStep(track) {
    const tile = track.querySelector(':scope > *');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return tile ? tile.getBoundingClientRect().width + gap : track.clientWidth;
  }

  #updateArrows = () => {
    const track = this.#track;
    const prev = this.querySelector('.evry-collection-slider__arrow--prev');
    const next = this.querySelector('.evry-collection-slider__arrow--next');
    if (!track || !prev || !next) return;

    const hasOverflow = track.scrollWidth > track.clientWidth + 1;
    prev.hidden = !hasOverflow || track.scrollLeft <= 1;
    next.hidden = !hasOverflow || track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
  };

  /** @param {MouseEvent} event */
  #handleArrowClick = (event) => {
    const target = event.target;
    const arrow = target instanceof Element && target.closest('.evry-collection-slider__arrow');
    if (!arrow || !this.#track) return;

    const direction = arrow.classList.contains('evry-collection-slider__arrow--prev') ? -1 : 1;
    // Not behavior: 'smooth' — see the identical note in cart-stack-carousel.liquid: an early
    // animation frame reads as a completed gesture against this track's own scroll-snap-type,
    // snapping straight back to the start instead of actually travelling.
    this.#track.scrollBy({ left: this.#tileStep(this.#track) * 2 * direction, behavior: 'instant' });
  };
}

if (!customElements.get('evry-collection-slider-component')) {
  customElements.define('evry-collection-slider-component', EvryCollectionSliderComponent);
}
